import React, { useState } from 'react';
import { FileTextIcon, CheckCircleIcon } from '../common/Icons';
import ConfirmDialog from '../common/ConfirmDialog';
import AlertDialog from '../common/AlertDialog';
import './UserDetailModal.css';

const XIcon = ({ className = '', size = 20 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const UserDetailModal = ({ user, documents = [], userType = 'employee', onClose, onVerify, onReject, onVerifyDocument, isLoading = false, userId }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showConfirmVerify, setShowConfirmVerify] = useState(false);
  const [showConfirmRequest, setShowConfirmRequest] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [documentToVerify, setDocumentToVerify] = useState(null);
  const [showConfirmDocumentVerify, setShowConfirmDocumentVerify] = useState(false);
  if (isLoading) {
    return (
      <div className="user-detail-modal-overlay" onClick={onClose}>
        <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="user-detail-modal__loading">
            <div className="user-detail-modal__spinner"></div>
            <p>Loading user details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDocumentClick = (documentUrl, e) => {
    // Prevent opening if clicking on action buttons
    if (e && (e.target.closest('.user-detail-modal__document-actions') || e.target.closest('button'))) {
      return;
    }
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const handleViewDocument = (documentUrl, e) => {
    e.stopPropagation();
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const handleDocumentApprove = (documentId, e) => {
    e.stopPropagation();
    setDocumentToVerify({ id: documentId, action: 'approve' });
    setShowConfirmDocumentVerify(true);
  };

  const handleDocumentReject = (documentId, e) => {
    e.stopPropagation();
    setDocumentToVerify({ id: documentId, action: 'reject' });
    setShowConfirmDocumentVerify(true);
  };

  const handleConfirmDocumentVerify = () => {
    if (documentToVerify && onVerifyDocument) {
      onVerifyDocument(documentToVerify.id, documentToVerify.action === 'approve', userId);
      setShowConfirmDocumentVerify(false);
      setDocumentToVerify(null);
    }
  };

  const getDocumentStatus = (doc) => {
    // By default, is_verified is false (not reviewed yet)
    // After admin approves: is_verified === true -> show "Approved"
    // After admin rejects: is_verified === false, but we can check if there's review metadata
    // If no review metadata, treat as pending (default state)
    
    if (doc.is_verified === true) return 'verified';
    
    // Check if document was explicitly reviewed and rejected
    // Backend might provide reviewed_at, verified_at, or other metadata when reviewed
    const hasReviewMetadata = doc.reviewed_at || doc.verified_at || doc.verification_date;
    if (doc.is_verified === false && hasReviewMetadata) {
      return 'rejected';
    }
    
    // Default state: not reviewed yet (pending) - don't show status badge
    return 'pending';
  };

  const hasDocumentBeenReviewed = (doc) => {
    // Document has been reviewed if:
    // 1. It's explicitly approved (is_verified === true)
    // 2. It has review metadata indicating it was reviewed (even if rejected)
    if (doc.is_verified === true || doc.is_verified_by_admin === true) return true;
    const hasReviewMetadata = doc.reviewed_at || doc.verified_at || doc.verification_date;
    return (doc.is_verified === false) && hasReviewMetadata;
  };

  const handleConfirmRequest = () => {
    setShowConfirmRequest(false);
    setAlertMessage('Document request sent successfully!');
    setShowAlert(true);
  };

  const handleVerify = () => {
    setShowConfirmVerify(true);
  };

  const handleConfirmVerify = () => {
    setShowConfirmVerify(false);
    onVerify(user.id);
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const handleRejectSubmit = () => {
    if (!rejectNote.trim()) {
      setAlertMessage('Please provide a reason for rejection.');
      setShowAlert(true);
      return;
    }
    onReject(user.id, rejectNote);
    setShowRejectModal(false);
    setRejectNote('');
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectNote('');
  };

  return (
    <div className="user-detail-modal-overlay" onClick={onClose}>
      <div className="user-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-detail-modal__header">
          <div className="user-detail-modal__header-content">
            <h2 className="user-detail-modal__title">
              {user.first_name} {user.last_name} - Details
            </h2>
            <span className={`user-detail-modal__status user-detail-modal__status--${user.is_verified_by_admin ? 'verified' : 'pending'}`}>
              {user.is_verified_by_admin ? 'Verified' : 'Pending Verification'}
            </span>
          </div>
          <button onClick={onClose} className="user-detail-modal__close">×</button>
        </div>

        <div className="user-detail-modal__content">
          {/* Personal Information */}
          <div className="user-detail-modal__section">
            <h3 className="user-detail-modal__section-title">Personal Information</h3>
            <div className="user-detail-modal__grid">
              <div className="user-detail-modal__field">
                <label>Full Name</label>
                <p>{user.first_name} {user.last_name}</p>
              </div>
              <div className="user-detail-modal__field">
                <label>Email</label>
                <p>{user.email}</p>
              </div>
              <div className="user-detail-modal__field">
                <label>Phone Number</label>
                <p>{user.contact_no || 'N/A'}</p>
              </div>
              <div className="user-detail-modal__field">
                <label>Date of Birth</label>
                <p>{user.dob ? formatDate(user.dob) : 'N/A'}</p>
              </div>
              <div className="user-detail-modal__field">
                <label>Address</label>
                <p>{user.address || 'N/A'}</p>
              </div>
              <div className="user-detail-modal__field">
                <label>Date Joined</label>
                <p>{formatDate(user.date_joined)}</p>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="user-detail-modal__section">
            <h3 className="user-detail-modal__section-title">Professional Information</h3>
            <div className="user-detail-modal__grid">
              {userType === 'employee' ? (
                <>
                  <div className="user-detail-modal__field">
                    <label>Job Role</label>
                    <p>{user.job_role_detail?.job_role_name || 'N/A'}</p>
                  </div>
                  <div className="user-detail-modal__field">
                    <label>GDC Number</label>
                    <p>{user.gdc_number || 'N/A'}</p>
                  </div>
                  <div className="user-detail-modal__field">
                    <label>Travel Miles</label>
                    <p>{user.travel_miles !== undefined ? `${user.travel_miles} miles` : 'N/A'}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="user-detail-modal__field">
                    <label>Registration Number</label>
                    <p>{user.registered_number || 'N/A'}</p>
                  </div>
                  <div className="user-detail-modal__field">
                    <label>Practice Name</label>
                    <p>{user.practice_name || 'N/A'}</p>
                  </div>
                  <div className="user-detail-modal__field">
                    <label>Practice Address</label>
                    <p>{user.address || 'N/A'}</p>
                  </div>
                </>
              )}
              <div className="user-detail-modal__field">
                <label>Email Verified</label>
                <p>
                  <span className={`user-detail-modal__badge user-detail-modal__badge--${user.is_email_verified ? 'success' : 'warning'}`}>
                    {user.is_email_verified ? 'Verified' : 'Not Verified'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="user-detail-modal__section">
            <h3 className="user-detail-modal__section-title">
              <FileTextIcon className="user-detail-modal__section-icon" size={20} />
              Uploaded Documents ({documents.length})
            </h3>
            {documents.length > 0 ? (
              <div className="user-detail-modal__documents">
                {documents.map((doc, index) => {
                  const status = getDocumentStatus(doc);
                  return (
                    <div 
                      key={doc.id || index} 
                      className="user-detail-modal__document-card"
                      onClick={(e) => handleDocumentClick(doc.document_url, e)}
                    >
                      <div className="user-detail-modal__document-icon">
                        <FileTextIcon size={24} />
                      </div>
                      <div className="user-detail-modal__document-info">
                        <h4 className="user-detail-modal__document-name">{doc.name || 'Document'}</h4>
                        <p className="user-detail-modal__document-meta">
                          {doc.uploaded_at ? `Uploaded: ${formatDate(doc.uploaded_at)}` : ''}
                        </p>
                        {/* Only show status badge if document has been reviewed (approved or rejected) */}
                        {hasDocumentBeenReviewed(doc) && (
                          <span className={`user-detail-modal__document-status user-detail-modal__document-status--${status}`}>
                            {status === 'verified' ? 'Approved' : 'Rejected'}
                          </span>
                        )}
                      </div>
                      <div className="user-detail-modal__document-actions" onClick={(e) => e.stopPropagation()}>
                        {doc.document_url && (
                          <button
                            className="user-detail-modal__document-btn user-detail-modal__document-btn--view"
                            onClick={(e) => handleViewDocument(doc.document_url, e)}
                            title="View Document"
                          >
                            <FileTextIcon size={18} />
                            View
                          </button>
                        )}
                        {onVerifyDocument && !user.is_verified_by_admin && doc.is_verified_by_admin === false && (
                          <>
                            {/* Show approve button only if not already approved and not rejected */}
                            {status !== 'verified' && status !== 'rejected' && (
                              <button
                                className="user-detail-modal__document-btn user-detail-modal__document-btn--approve"
                                onClick={(e) => handleDocumentApprove(doc.document_id, e)}
                                title="Approve Document"
                              >
                                <CheckCircleIcon size={18} />
                                Approve
                              </button>
                            )}
                            {/* Show reject button only if not already approved */}
                            {/* Once rejected, don't show approve button anymore */}
                            {status !== 'verified' && (
                              <button
                                className="user-detail-modal__document-btn user-detail-modal__document-btn--reject"
                                onClick={(e) => handleDocumentReject(doc.document_id, e)}
                                title="Reject Document"
                              >
                                <XIcon size={18} />
                                Reject
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="user-detail-modal__empty">
                <p>No documents uploaded yet</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="user-detail-modal__actions">
            {!user.is_verified_by_admin && (
              <>
                <button
                  onClick={handleVerify}
                  className="user-detail-modal__btn user-detail-modal__btn--verify"
                >
                  Verify User
                </button>
                <button
                  onClick={handleRejectClick}
                  className="user-detail-modal__btn user-detail-modal__btn--reject"
                >
                  Reject with Note
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Verify Dialog */}
      <ConfirmDialog
        isOpen={showConfirmVerify}
        title="Verify User"
        message="Are you sure you want to verify this user? This action cannot be undone."
        confirmText="Verify"
        cancelText="Cancel"
        type="success"
        onConfirm={handleConfirmVerify}
        onCancel={() => setShowConfirmVerify(false)}
      />

      {/* Confirm Document Verify Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDocumentVerify}
        title={documentToVerify?.action === 'approve' ? 'Approve Document' : 'Reject Document'}
        message={`Are you sure you want to ${documentToVerify?.action === 'approve' ? 'approve' : 'reject'} this document?`}
        confirmText={documentToVerify?.action === 'approve' ? 'Approve' : 'Reject'}
        cancelText="Cancel"
        type={documentToVerify?.action === 'approve' ? 'success' : 'info'}
        onConfirm={handleConfirmDocumentVerify}
        onCancel={() => {
          setShowConfirmDocumentVerify(false);
          setDocumentToVerify(null);
        }}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={showAlert}
        title="Notification"
        message={alertMessage}
        buttonText="OK"
        type="info"
        onClose={() => setShowAlert(false)}
      />

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="user-detail-modal__reject-overlay" onClick={handleRejectCancel}>
          <div className="user-detail-modal__reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-detail-modal__reject-header">
              <h3>Reject User</h3>
              <button onClick={handleRejectCancel} className="user-detail-modal__reject-close">×</button>
            </div>
            <div className="user-detail-modal__reject-content">
              <p className="user-detail-modal__reject-label">Please provide a reason for rejection:</p>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Enter rejection reason..."
                className="user-detail-modal__reject-textarea"
                rows="5"
              />
            </div>
            <div className="user-detail-modal__reject-actions">
              <button
                onClick={handleRejectCancel}
                className="user-detail-modal__reject-btn user-detail-modal__reject-btn--cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                className="user-detail-modal__reject-btn user-detail-modal__reject-btn--submit"
                disabled={!rejectNote.trim()}
              >
                Reject User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailModal;

